/**
 * Client i18n (web-client.md §13; implementation-plan §8 item 2).
 *
 * The site is Russian-first and the content ships `ru` strings for every card; the NPC voices and the
 * retro already answer in the room's language. The interface did not, which made the mismatch MORE
 * visible than it was before the model half landed, not less - and help that ships English-only to a
 * RU table is the worst possible place to be untranslated.
 *
 * Deliberately not a library. The whole surface is one process, one language per session, a few hundred
 * short strings and no pluralisation rules worth the dependency:
 *
 * - **Keys are dotted and grouped by surface** (`login.help.create`, `board.roster`), so a missing
 *   translation is findable by reading rather than by running the client in RU.
 * - **English IS the fallback.** A key with no RU entry renders the EN one rather than the key itself:
 *   a player reading one English line in a Russian screen has a worse day than the translator does, but
 *   a player reading `board.roster` has no day at all. A test asserts RU covers every key, so the
 *   fallback is a safety net rather than a plan.
 * - **Interpolation is `{name}`**, filled from a plain object. Anything cleverer would be a template
 *   language nobody asked for.
 */
export type Lang = 'en' | 'ru';

/** The English catalog. It is also the key list: RU is checked against it in tests. */
const EN = {
  // ---- the lobby / login view --------------------------------------------------------------------
  'login.title': 'TEAM SIMULATOR — web client (S0)',
  'login.status': 'connection: {connection}   player: {player}   {version}',
  'login.notSignedIn': '(not signed in)',
  'login.begin': 'Type "login" (optionally with a display name) to begin.',
  'login.signedIn': 'Signed in. Create a session or join one below.',
  // DEC-18: your rooms first, the directory second.
  'login.mySessions': 'your sessions:',
  'login.publicSessions': 'public sessions:',
  'login.noPublicOthers': 'public sessions: (none open right now)',
  'login.noPublicSessions': 'public sessions: (none — type "list" to refresh)',
  'login.commands': 'commands:',
  'login.help.login': 'login [name]                 – sign in (no name = come back as yourself)',
  // DEC-18: sessions are private by default, so the flag with a consequence is now "pub".
  'login.help.create':
    'create <name> [game [deck]] [pub] – create a session, private ("game" runs a scenario, "pub" lists it)',
  // DEC-17 M4 increment 8b: four of the five shipped decks could not be started from any client.
  'login.help.decks':
    'decks                        – list the scenarios "create ... game <deck>" accepts',
  'login.decks': 'decks:',
  'login.noDecks': 'decks: (none listed yet – type "list" to refresh the catalog)',
  'login.deckLine': '  {ref} – {title} ({shape}, {seats} seats)',
  'login.deckExcerpt': '{rounds}-round scenario',
  'login.deckSim': 'open-ended sim',
  'login.unknownDeck':
    'no scenario "{deck}" – type "decks" to see them, then: create <name> game <deck>',
  'login.help.join': 'join <id> [code]             – join a session by id (+ code if private)',
  'login.help.list': 'list                         – refresh the public session list',
  'login.help.number': '<number>                     – join the Nth listed session',
  'login.help.diag': 'diag                         – open the diagnostics doctor page',
  'login.help.reload': 'reload                       – apply a pending update',
  'login.help.help': 'help                         – show this help',
  'login.help.share':
    'share                        – link that joins the active session on another device',
  'login.noSuchListed': 'no session #{n} in the list – type "list" to refresh it',
  'login.usage.join': 'usage: join <sessionId> [joinCode]',
  'login.unknown': 'unknown command: {cmd} (type "help")',
  'login.sessionCommand':
    '/{cmd} works inside a session – you are in the lobby: type "login", then join or create one',

  // ---- version readout ----------------------------------------------------------------------------
  'version.unknown': 'v{running} (latest unknown)',
  'version.upToDate': 'v{running} (up to date)',
  'version.behind': 'v{running} → latest v{latest}',

  // ---- the session board --------------------------------------------------------------------------
  'session.title': 'SESSION — {name}',
  'session.status':
    'connection: {connection}   you: {player}   panel: {panel} (/view {panels})   {version}   /leave to exit',
  'session.readOnly': 'reconnecting… (read-only cached view — input disabled)',
  'board.roster': 'roster:',
  'board.rosterEmpty': '  (empty)',
  'board.chat': 'chat:',
  // The outbox: your own line, on screen before the server has confirmed it.
  'chat.sending': '  … {text}',
  'chat.failed': '  ✗ {text}  – did not send ({error}); /retry {id}, or /discard {id}',
  'chat.pendingList': 'not sent: {ids} – /retry <id> to send again, /discard <id> to drop it',
  'chat.nonePending': 'nothing is waiting to send.',
  'app.noSuchPending':
    'nothing waiting under {id} – type /retry with the id printed beside the line',
  'board.chatEmpty': '  (no messages yet — say hello)',
  'board.chatEarlier': '  … {n} earlier (/view chat)',
  'board.timeline': 'timeline:',
  'board.timelineEmpty': '  (nothing has happened yet)',

  // ---- contextual help (session-help.md §3.2) -----------------------------------------------------
  'help.noBoard': 'you: no game board yet',
  'help.noBoard.action': '  this session runs no scenario – /leave, then: create <name> game',
  'help.you': 'you: {seat} ({checkIn} this round, free capacity {capacity})',
  'help.noSeat': 'you: no seat yet',
  'help.verbs': '  your verbs: {verbs}',
  'help.takeSeat': '  take a seat: {seats}',
  'help.allSeatsTaken': '  every seat is taken – /view board to see who holds what',
  'help.notStarted': '  this round: the game has not started – /start',
  'help.checkIn': '  this round: /checkin {seat} closes your turn',
  'help.pending': '  pending: {items}',
  'help.pending.event': 'event {id} – /resolve {options}',
  'help.pending.decision': 'decision {id} due r{round} – /decide {id} {options}',
  'help.scene': '  scene {kind}/{beat}{prompt} · /beat to advance',
  'help.scenes': '  scenes: /scene standup · /scene planning · /scene governance',
  'help.talk': '  talk: @<seat> <message> to the room · /ask {seat} mood privately',
  'help.footer': '  what is this session: /brief · full list: /commands · seats: /view guide',

  // ---- the seat guide (session-help.md §3.4) ------------------------------------------------------
  'guide.title': 'guide - the seats and the round',
  'guide.noBoard': '  no game board yet - this session runs no scenario',
  'guide.you': '(you)',
  'guide.bot': '(a bot plays this seat)',
  // The scenario authored a person for this seat; say who, and keep the fact that nobody human holds it.
  'guide.botNamed': '({name} – played by a bot)',
  // DEC-16: a seat with no human is a COLLEAGUE, not a vacancy. Since the NPC-characters work every
  // humanless seat is NPC-occupied at initialize, so "(free)" described a chair that was never empty.
  // What survives is TAKEOVER - a late human can still step into a seat no human holds.
  'guide.free': '(nobody has taken this one yet)',
  'guide.freeNamed': '({name} – nobody human has taken this one)',
  'guide.verbs': '    verbs: {verbs}',
  'guide.verbsLocked': '    verbs: {verbs} (claim the seat to use them)',
  'guide.owns': '    owns: {decisions}',
  'guide.share': '    at standup: /share {seat} <item>',
  'guide.ask': '    ask it: /ask {seat} mood',
  'guide.round': '  the round:',
  'guide.round.checkin': '    /checkin <seat> closes your turn',
  'guide.round.event': '    → the director may fire an event (/resolve)',
  'guide.round.decisions': '    → decisions come due (/decide · /defer · /vote)',
  'guide.round.scenes':
    '    → scenes when you call them (/scene standup|planning|governance · /beat)',
  'guide.footer': '  commands by object: /commands · what now: /help',

  // ---- the command catalog ------------------------------------------------------------------------
  'commands.header': 'commands (anything not starting with / is chat):',
  'commands.group.seat': 'your seat',
  'commands.group.work': 'the work',
  'commands.group.okrs': 'okrs',
  'commands.group.decisions': 'decisions',
  'commands.group.policy': 'concerns & policy',
  'commands.group.scenes': 'scenes',
  'commands.group.talking': 'talking',
  'commands.group.session': 'the session',
  'commands.group.other': 'other',

  // ---- refusals and notices (standards/messages.md: each names the next move) ----------------------
  'msg.noSeatNamed': 'no seat named {seat} - seats: {seats}',
  'msg.mentionUsage': 'usage: {usage}',
  'msg.npcMention': '{seat} is played by a bot – ask it anything with /ask {seat} <your question>',
  'msg.askUsage': 'usage: /ask <seat> <your question, or mood|decision:id|concern:id>',
  'msg.viewUsage': 'usage: /view {panels} (now: {panel})',
  'msg.panelSwitched': 'panel: {panel}',
  'msg.noPendingEvent':
    'no pending event to resolve – the board names one under "event:" when the director fires it',
  'msg.doUsage': 'usage: /do <verb> [seat] – yours: {verbs}',
  'msg.doNoSeat': 'usage: /do <verb> [seat] – you hold no seat; /claim <seat> first',
  'msg.doNeedsSeat': '/do {verb} needs a seat – you hold none, so name one: /do {verb} <seat>',
  'msg.usage': 'usage: {usage}',
  'msg.nearMiss': 'no /{cmd} – you probably want: {meant}',
  'msg.unknownCommand':
    'unknown command: {text} – /help for what you can do now, /commands for the full list',

  // ---- app-level lines ----------------------------------------------------------------------------
  'app.welcome': 'welcome — type "login" to begin.  (type "diag" for diagnostics)',
  'app.server': 'server: {url}',
  'app.restored':
    'restored a cached session (read-only) – type "login" to sign back in and open it',
  'app.signedIn': 'signed in as {player}',
  'app.resumed': 'welcome back, {player} – same player as last time',
  'app.loginFailed':
    'login failed: {error} – type "login" to try again, or "diag" if it keeps failing',
  'app.loginFirst': 'login first (type "login")',
  'app.created': 'created "{name}" — {id}',
  'app.joinCode': 'join code: {code}',
  'app.shareHint': 'type "share" for a link that joins this session on another device',
  'app.createFailed': 'create failed: {error} – try again, or type "diag" if it keeps failing',
  'app.joined': 'joined {id}',
  'app.joinFailed':
    'join failed: {error} – check the id and join code, or type "list" for public sessions',
  'app.usage.join': 'usage: join <sessionId> [joinCode]',
  'app.shareFirst': 'open a session first, then type "share"',
  'app.shareLink': 'share this link: {link}',
  'app.shareNote':
    'opening it on another device joins this session and resumes from where it is now.',
  'app.sharePrivate': '(private session? the owner is the only one who sees its join code)',
  'app.invitation': 'invitation: session {id}',
  'app.copied': '(copied to clipboard)',
  'app.listed': '{mine} of yours, {n} public session(s)',
  'app.listFailed': 'list failed: {error} – type "list" to retry, or "diag" if it keeps failing',
  'app.reconnecting': 'reconnecting… (read-only until the server confirms)',
  'app.noActiveSession': 'no active session – type "list" then a number, or: create <name> game',
  'app.sendFailed': 'send failed: {error} – nothing was sent; say it again once the bar reads live',
  'app.askFailed': 'could not ask {seat}: {error}',
  'app.retroFailed': 'retro failed: {error} – type /retro again in a moment',
  'app.notAGame':
    '  this session was created without a scenario – /leave, then: create <name> game',
  'app.leaveFailed': 'leave failed: {error} – leaving this client anyway',
  'app.diagUnavailable':
    'diagnostics unavailable in this environment – nothing to do here; the doctor page needs a browser URL bar',
  'app.reloading': 'reloading to apply the update…',
  'app.updateReady': 'a new version is ready — type "reload" to update.',
  'app.updateAvailable': 'update available: {instructions}',
  'app.updateRequired': 'update required: {reason} — reloading…',
  'app.tokenRefreshFailed': 'token refresh failed — please login again',
  'app.sessionExpired': 'session expired — please login again',
  'app.authRefreshing': 'auth: {code} — refreshing',
  // Identity hygiene (playtest one). Both warnings name the move that KEEPS what you have, and the
  // one that goes ahead anyway - because both are legitimate and the client does not get a vote.
  'app.nameLooksLikeCommand':
    '"{name}" looks like a command, not a name – it would become your display name to the whole table.',
  'app.identityExists':
    'you already have an identity in this browser ({player}, in {teams} team(s)) – a new one loses its seats. Type "login" with no name to keep it.',
  'app.identityConfirm': 'to use it anyway, type: login {name}',
  'app.notice': 'notice: {message}',
  // The server states the fact; the client names the move in its own vocabulary (messages.md §4).
  // A seat is `/claim`ed here and clicked in the workspace, which is exactly why the server does not
  // try to spell the action itself.
  'app.noticeClaim': 'notice: {message} Type: /claim {seat}',
  // The observer landing (DEC-19). The server says "start a session of your own"; only the client
  // knows that here it is spelled `create <name> game` (messages.md §2.2: concrete enough to type).
  'app.noticeObserver':
    'notice: {message} Type "help" for what you can still do, or: create <name> game',
  'app.gameRefused': 'game: {message}',
  'app.gameActionFailed': 'game action failed: {error}',

  // ---- scene beats (the engine's beat -> moves mirror) --------------------------------------------
  'beat.digest': 'what happened since the last standup (below)',
  'beat.reveals': 'each seat may surface one private item — /share <seat> <item>',
  'beat.agenda': 'walk the decision queue — /decide /defer /consult /vote /approve',
  'beat.commitments': 'who is doing what — /work <item> <seat>',
  'beat.objective': 'write the objective — /objective <text>',
  'beat.keyResults': 'add the key results measuring it — /kr <id> <expression>',
  'beat.review': 'read the board back before you commit to it',
  'beat.docket': 'raise the concerns worth meeting about — /concern <seat> <topic>',
  'beat.motions': 'raise decisions against the docket — /raise <id>',
  'beat.deliberation': 'consult, vote, sign off — /consult /vote /approve',
  'beat.resolution': 'close what you can — /decide <id> <option>',
  'beat.close': 'mood pulse and out — /mood <seat> <mood>, then /beat',

  // ---- the digest window --------------------------------------------------------------------------
  'digest.quiet': '    since your last standup: nothing happened.',
  'digest.since': '    since round {round}: {parts}',
  'digest.also': '    also: {parts}',
  'digest.rounds': '{n} round(s)',
  'digest.events': '{n} event(s) resolved',
  'digest.decisions': '{n} decision(s) closed',
  'digest.work': '{n} item(s) shipped',
  'digest.hires': '{n} hire(s) landed',
  'digest.fuses': '{n} delayed effect(s) landed',
  'digest.timedOut': '{n} decision(s) timed out into the worst case',
  'digest.escalated': '{n} concern(s) escalated',
  'digest.promises': '{n} promise(s) broken',

  // ---- the decision queue -------------------------------------------------------------------------
  'decisions.title': 'decisions:',
  'decisions.none': '  (none open) — /raise <id> [seat] opens an authored call',
  'decisions.options': '    options: {options} (worst case: {worst} if it times out)',
  'decisions.needs': '    needs: {blocker}',
  'decisions.blocker.consults': '{n} more consult(s) — /consult {id} <seat>',
  'decisions.blocker.votes': '{n} more vote(s) for quorum — /vote {id} <seat> <option>',
  'decisions.blocker.approval': 'sign-off from {approver} — /approve {id} <seat>',
  'decisions.approver': 'the approver',

  // ---- the policy ledger --------------------------------------------------------------------------
  'policies.proposed': '  proposed (not adopted yet):',
  'policies.proposedName': '    {name} ({id})',
  'policies.proposedProse': '      {prose}',
  'policies.proposedEffects': '      each round: {effects}',
  'policies.proposedDownside': '      costs: {effects}',
  'policies.proposedAdopt': '      /adopt {id} puts it in force',
  'policies.title': 'policies:',
  'policies.none': '  (nothing adopted) — policies arrive by resolving a motion',
  'policies.repealed': '    repealed r{round}',

  // ---- the personal panel -------------------------------------------------------------------------
  'me.title': 'you:',
  'me.quietWords': 'quiet words (only you see these):',
  'me.asked': '  you asked {seat} about {about}',
  'me.heard': '    they heard: “{quote}”',
  'me.noSeat': '  no seat yet — /claim <seat> to take one',
  // DEC-16: after the day starts, the same input is a takeover rather than a claim.
  'me.takeover': '  you are watching. You can step into a role somebody is covering:',
  'me.takeoverFrom': 'take over as {title}, from {who}',
  'me.noVerbs': 'no verbs',
  'me.seat': '  seat {seat} · free capacity {capacity} · {checkIn}',

  // ---- the game board -----------------------------------------------------------------------------
  'board.header': 'game – round {round}{clock} · {phase} · {status}',
  'board.situation':
    '  situation: risk {risk} · concerns {concerns}{heat} · wip {wip} · reqs {reqs} · decisions {decisions} · policies {policies}',
  // The goal (first-run-review.md §A1). `win` lists what ends the session well; `lose` is rendered in
  // SURVIVAL form ("trust 54>0" = keep it above zero), which is why its separator is "and", not "or".
  'board.win': '  win: {conditions}',
  'board.lose': '  lose: {conditions}',
  // DEC-17 M4: in an endless frame the floors are not how you lose, they are how the company ends.
  'board.collapse': '  collapse: {conditions}',
  'board.endless': '  no deadline – the company runs until it stops',
  'board.advance': '  advance to {phase}: {conditions}',
  // The milestone chain (DEC-17 M1). The terminal has no calendar, so this line is the calendar: where
  // the company has been, where it is, and what is still ahead of it.
  'board.milestone': '  milestones: {chain}',
  'board.milestoneHere': '[{name}]',
  'board.milestoneCrossed': '{name}: day {day}',
  'board.milestoneUnlocks': '  {name} opens: {unlocks}',
  // `·` reads as "and" everywhere on this line; only the alternatives need a word.
  'board.goalOr': ' or ',
  'board.heat': ' (heat {n})',
  // DEC-17 M2: a fuse whose clock is a condition. What arms it, not how long is left.
  'board.armed': '  {metric} lands when {condition}',
  'board.sceneOffered':
    '  {kind} is on the table — /beat to walk it, or carry on and it will close itself',
  'board.scene': '  scene: {kind} — {beat} ({index}/{count}){shared}',
  'board.sceneShared': ', shared {n}',
  'board.thisBeat': '    this beat: {prompt}',
  'board.seats': '  seats:',
  'board.can': '  can: {verbs}',
  // DEC-16: entry asks who you are. The picker leads with the deck's own recommendation
  // (`npcEligible: false` - the seat the scenario will not let a bot hold) and pitches it in the
  // scenario's own words, so the question is answerable by somebody who has never read the repo.
  'board.whoAreYou': '  who are you on this team?',
  'board.pickRole': '    {title} — /claim {seat}',
  'board.pickRolePitched': '    {title} — {pitch}   /claim {seat}',
  'board.pickRecommended': '    {title} — {pitch}   /claim {seat}   ← start here',
  'board.takeoverOnly': '  every role is held by somebody. You are watching this one.',
  'board.backlog': '  backlog:',
  'board.company': '  {name}',

  // The outside world (§2a). Banded, never a number: patience is the meter that libels if charted.
  'board.promise': '  you promised {who}: {what} — by r{round}   /kept {id} · /broke {id}',
  'board.promiseOverdue':
    '  ⚠ overdue to {who}: {what} — was due r{round}   /kept {id} · /broke {id}',
  'board.stakeholder': '  {who}: {band}',
  'board.stakeholderSteady': 'no complaints so far',
  'board.stakeholderImpatient': 'starting to push',
  'board.stakeholderSpent': 'out of patience',

  // The calendar line (workspace-client.md §2a): the schedule's own answer to "what is coming", so a
  // scheduled meeting stops arriving as a surprise.
  'board.upcoming': '  next up: {scene} in {rounds} round(s)',
  'board.upcomingNow': '  next up: {scene} when this round opens',
  'board.event': '  event: {id}',
  // Only the heavy tiers are marked. "texture" on a small card is noise; an unmarked consequence hides
  // that the table caused this one (game-loop §9.1 class 1).
  'board.eventConsequence': '  ← this one follows from an earlier call',
  'board.eventScripted': '  ← this one was always coming',
  'board.eventReveal': '    only you see: {reveal}',
  'board.eventOption': '    /resolve {id}  {label}{odds}',
  'board.eventUnlabelled': '(unlabelled)',
  'board.bet': ' [bet – {odds}]',
  'board.oddsUnstated': 'odds unstated',
  'board.decisions': '  decisions: {summary} — /view decisions',
  'board.policyProposed':
    '  proposed rule: {names} — /view policies to read it before it is adopted',
  'board.decisionYours': 'yours',
  'board.okrs': '  okrs:',
  'board.objective': '    objective: {objective}',
  'board.you': '  you: {seat} (free {capacity}, {checkIn})',

  // ---- fidelity, streams and the retro ------------------------------------------------------------
  'fidelity.line': 'fidelity: {rung} – spent {spent} of {ceiling} ({percent}%)',
  'fidelity.disabled': '  no longer running: {purposes}',
  'milestone.header': '{headline}',
  'milestone.scripted': '  (composed from the engine\u2019s own record)',
  'milestone.writing': '  writing it up\u2026',
  'milestone.none': 'no milestones crossed yet.',
  'retro.header': 'retro – {headline}',
  // The survival score, shown as its four terms (DEC-17 M4 §3): one number hides four achievements.
  'retro.survival':
    '  survival {score} = final {final} + peak {peak}×{peakWeight} + {rounds} rounds×{roundWeight} + {gates} milestones×{gateWeight} (triangular)',
  'retro.okrs': '  OKRs: {grades}',
  'retro.concerns': '  concerns: {concerns}',
  'retro.motifs': '  motifs: {motifs}',
  'retro.private': 'your retro (only you see this):',

  // ---- what to do about a refusal (standards/messages.md; views/refusals.ts) -----------------------
  'refuse.noOpenScene': '  no scene is open – /scene standup · /scene planning · /scene governance',
  'refuse.sceneAlreadyOpen': '  a scene is already running – /beat advances it to the next part',
  'refuse.notInStandup': '  that move belongs to a standup – /scene standup opens one',
  'refuse.slotUsed': '  you have already used your share slot this standup',
  'refuse.seatTaken':
    '  {value} is taken – /view guide shows who holds what and which seats are free',
  'refuse.alreadySeated': '  you already hold a seat – /release it before taking another',
  'refuse.notSeated':
    '  you hold no seat – /claim <seat>, and /view guide lists what each one does',
  'refuse.unknownSeat': '  no seat by that name – /view guide lists them',
  'refuse.overCapacity': '  that seat has no capacity left this round – /checkin closes the round',
  'refuse.sessionStarted': '  the session is already running – /help shows what you can do in it',
  'refuse.noOpenGate': '  nothing is waiting on a gate right now',
  'refuse.krDuplicate': '  a key result already has that id – pick another, or /rmkr {value} first',
  'refuse.tooManyKrs': '  the board is full of key results – /rmkr <id> to make room',
  'refuse.unknownKr': '  no key result by that id – the board lists them under "okrs:"',
  'refuse.krGraded': '  that key result is already graded – graded results are history, not state',
  'refuse.decisionNotOwner': '  you do not own that call – /view decisions names its owner',
  'refuse.decisionUnknown': '  no decision by that id – /view decisions shows the open ones',
  'refuse.decisionUnknownOption': '  that is not one of its options – /view decisions lists them',
  'refuse.decisionConsults': '  it still needs consults – /consult <id> <seat>',
  'refuse.decisionQuorum': '  it still needs votes – /vote <id> <seat> <option>',
  'refuse.decisionApproval': '  it is waiting on sign-off – /approve <id> <seat>',
  'refuse.workUnknown': '  no backlog item by that id – the board lists them under "backlog:"',
  'refuse.workDuplicate': '  that backlog id is taken – pick another',
  'refuse.workNotStartable': '  that item cannot start yet – /view board shows its state',
  'refuse.backlogFull': '  the backlog is full – /abandon <id> to make room',
  'refuse.notAGame': '  this session runs no scenario – /leave, then: create <name> game',

  // ---- the planning gate's OKR compiler (S2.4 §3.3) -----------------------------------------------
  'okr.none': 'nothing gradable came back – write the key result yourself: /kr <id> <expression>',
  'okr.objective': 'objective: {objective}   (adopt with: /objective {objective})',
  'okr.keyResult': '  {description}',
  'okr.adopt': '    /kr {id} {expression}',
  'okr.coaching': '  Ines (consultant): {coaching}',
  'okr.rejected':
    "  ({n} suggestion(s) dropped: they did not compile against this scenario's metrics)",
  'okr.failed': 'could not compile that goal: {error}',
  'okr.usage': 'usage: /okr <what the team is trying to achieve>',
  'policy.preview': 'proposed policy: {name} ({id})',
  'policy.prose': '  {prose}',
  'policy.effects': '  it does: {effects}',
  'policy.downside': '  it costs: {effects}',
  'policy.adopt': '  adopt it with: /adopt {id}',
  'policy.refused': 'that agreement did not compile into a rule this session can hold:',
  'policy.reason': '  {rule}: {message}',
  'policy.unavailable': 'policy compilation is unavailable right now – try again in a moment',
  'policy.failed': 'could not compile that policy: {error}',
  'policy.noDraft': 'no previewed policy called {id} – /policy <what the team agrees> drafts one',
  'policy.usage': 'usage: /policy <the agreement, in your own words>',
  'policy.adoptUsage': 'usage: /adopt <id of a previewed policy>',
  'brief.title': 'this session:',
  'brief.deadline':
    '  the clock: round {round} of {deadline} - when it runs out, the session ends where it stands',
  'brief.noDeadline': '  the clock: round {round}, no fixed deadline',
  'brief.objective': '  the objective: {objective}',
  'brief.noObjective':
    '  the objective: nobody has set one yet – /objective <text> at the planning gate',
  'brief.who': '  people: {humans}   ·   bots: {bots}',
  // The bars themselves, so a group can answer "what are we trying to do" without reading YAML.
  'brief.win': '  to win: {conditions}',
  'brief.lose': '  keep true: {conditions}',
  'brief.ends':
    '  it ends when the deadline runs out or the scenario resolves; the retro then tells you what happened',
  'brief.footer': '  what can I do now: /help   ·   what is each seat for: /view guide',
  'chrome.you': 'you: {seat} ({checkIn})',
  'chrome.noSeat': 'no seat',
  'board.claim': '[take this role]',
  'chrome.clock': 'r{round} · {phase}',
  'app.retryAfter': '  too many requests – wait {seconds}s and try again',

  // ---- the diagnostics doctor (web-client.md §8) --------------------------------------------------
  'diag.unknown': 'unknown: {cmd} (run · repair · bundle · back)',
  'diag.update': 'update: {action} ({reason})',
  'diag.cachesCleared': 'caches cleared (IndexedDB). Reload to re-fetch.',
  'diag.repairFailed': 'repair failed: {error} – reload the page, then run repair again',
  'diag.bundleDownloaded': 'sanitized bundle downloaded (no tokens/PII).',
  'diag.bundleReady': 'bundle ready ({bytes} bytes, redacted).',
  'diag.title': 'TEAM SIMULATOR — diagnostics (doctor)',
  'diag.server': 'server: {url}',
  'diag.typeRun': 'type "run" to start.',
  'diag.summary': 'summary: {pass} ok · {warn} warn · {fail} fail',
  'diag.commands': 'commands: run · repair (clear caches) · bundle (download) · back',
} as const;

export type MessageKey = keyof typeof EN;

/**
 * The Russian catalog. Deliberately complete rather than partial: a half-translated screen is the state
 * this work exists to remove, and the EN fallback would hide the gap from every test.
 *
 * Command names, ids and panel keys stay Latin - they are typed, not read, and a player who reads
 * `/decide` translated cannot type what they see.
 */
const RU: Partial<Record<MessageKey, string>> = {
  'login.title': 'TEAM SIMULATOR — веб-клиент (S0)',
  'login.status': 'соединение: {connection}   игрок: {player}   {version}',
  'login.notSignedIn': '(не выполнен вход)',
  'login.begin': 'Введите "login" (можно с отображаемым именем), чтобы начать.',
  'login.signedIn': 'Вход выполнен. Создайте сессию или присоединитесь к одной из списка.',
  'login.mySessions': 'ваши сессии:',
  'login.publicSessions': 'открытые сессии:',
  'login.noPublicOthers': 'открытые сессии: (сейчас нет)',
  'login.noPublicSessions': 'открытые сессии: (нет — введите "list", чтобы обновить)',
  'login.commands': 'команды:',
  'login.help.login': 'login [имя]                  – войти (без имени = вернуться собой)',
  'login.help.create':
    'create <имя> [game [колода]] [pub] – создать закрытую сессию ("game" — сценарий, "pub" — в каталог)',
  'login.help.decks':
    'decks                        – список сценариев для "create ... game <колода>"',
  'login.decks': 'сценарии:',
  'login.noDecks': 'сценарии: (пока пусто – наберите "list", чтобы обновить каталог)',
  'login.deckLine': '  {ref} – {title} ({shape}, мест: {seats})',
  'login.deckExcerpt': 'сценарий на {rounds} раундов',
  'login.deckSim': 'бесконечный сим',
  'login.unknownDeck':
    'нет сценария «{deck}» – наберите "decks", затем: create <имя> game <колода>',
  'login.help.join': 'join <id> [код]              – войти в сессию по id (+ код, если закрытая)',
  'login.help.list': 'list                         – обновить список открытых сессий',
  'login.help.number': '<число>                      – войти в N-ю сессию из списка',
  'login.help.diag': 'diag                         – открыть страницу диагностики',
  'login.help.reload': 'reload                       – применить обновление',
  'login.help.help': 'help                         – показать эту справку',
  'login.help.share':
    'share                        – ссылка, открывающая эту сессию на другом устройстве',
  'login.noSuchListed': 'в списке нет сессии №{n} – введите "list", чтобы обновить его',
  'login.usage.join': 'как использовать: join <sessionId> [joinCode]',
  'login.unknown': 'неизвестная команда: {cmd} (введите "help")',
  'login.sessionCommand':
    '/{cmd} работает внутри сессии – вы в лобби: введите "login", затем войдите в сессию или создайте её',

  'version.unknown': 'v{running} (последняя версия неизвестна)',
  'version.upToDate': 'v{running} (актуальная)',
  'version.behind': 'v{running} → доступна v{latest}',

  'session.title': 'СЕССИЯ — {name}',
  'session.status':
    'соединение: {connection}   вы: {player}   панель: {panel} (/view {panels})   {version}   /leave — выйти',
  'session.readOnly': 'переподключение… (кэш только для чтения — ввод отключён)',
  'board.roster': 'состав:',
  'board.rosterEmpty': '  (пусто)',
  'board.chat': 'чат:',
  'chat.sending': '  … {text}',
  'chat.failed': '  ✗ {text}  – не ушло ({error}); /retry {id} или /discard {id}',
  'chat.pendingList': 'не ушло: {ids} – /retry <id> отправить снова, /discard <id> убрать',
  'chat.nonePending': 'ничего не ждёт отправки.',
  'app.noSuchPending': 'под {id} ничего не ждёт – укажите id, напечатанный рядом со строкой',
  'board.chatEmpty': '  (сообщений пока нет — поздоровайтесь)',
  'board.chatEarlier': '  … ещё {n} выше (/view chat)',
  'board.timeline': 'хроника:',
  'board.timelineEmpty': '  (пока ничего не произошло)',

  'help.noBoard': 'вы: игрового поля пока нет',
  'help.noBoard.action': '  в этой сессии не запущен сценарий – /leave, затем: create <имя> game',
  'help.you': 'вы: {seat} ({checkIn} в этом раунде, свободная ёмкость {capacity})',
  'help.noSeat': 'вы: роль пока не занята',
  'help.verbs': '  ваши действия: {verbs}',
  'help.takeSeat': '  занять роль: {seats}',
  'help.allSeatsTaken': '  все роли заняты – /view board, чтобы увидеть, кто где',
  'help.notStarted': '  этот раунд: игра ещё не начата – /start',
  'help.checkIn': '  этот раунд: /checkin {seat} закрывает ваш ход',
  'help.pending': '  ждёт вас: {items}',
  'help.pending.event': 'событие {id} – /resolve {options}',
  'help.pending.decision': 'решение {id}, срок r{round} – /decide {id} {options}',
  'help.scene': '  сцена {kind}/{beat}{prompt} · /beat — дальше',
  'help.scenes': '  сцены: /scene standup · /scene planning · /scene governance',
  'help.talk': '  разговор: @<роль> <текст> — всей команде · /ask {seat} mood — лично',
  'help.footer': '  что это за сессия: /brief · весь список: /commands · роли: /view guide',

  'guide.title': 'справочник — роли и раунд',
  'guide.noBoard': '  игрового поля пока нет — в этой сессии не запущен сценарий',
  'guide.you': '(вы)',
  'guide.bot': '(за эту роль играет бот)',
  'guide.botNamed': '({name} – за него играет бот)',
  'guide.free': '(её пока никто не занял)',
  'guide.freeNamed': '({name} – её пока никто из людей не занял)',
  'guide.verbs': '    действия: {verbs}',
  'guide.verbsLocked': '    действия: {verbs} (займите роль, чтобы использовать)',
  'guide.owns': '    отвечает за: {decisions}',
  'guide.share': '    на стендапе: /share {seat} <item>',
  'guide.ask': '    спросить: /ask {seat} mood',
  'guide.round': '  раунд:',
  'guide.round.checkin': '    /checkin <роль> закрывает ваш ход',
  'guide.round.event': '    → директор может запустить событие (/resolve)',
  'guide.round.decisions': '    → наступают сроки решений (/decide · /defer · /vote)',
  'guide.round.scenes':
    '    → сцены, когда вы их созываете (/scene standup|planning|governance · /beat)',
  'guide.footer': '  команды по объектам: /commands · что сейчас: /help',

  'commands.header': 'команды (всё, что не начинается с /, уходит в чат):',
  'commands.group.seat': 'ваша роль',
  'commands.group.work': 'работа',
  'commands.group.okrs': 'цели',
  'commands.group.decisions': 'решения',
  'commands.group.policy': 'риски и правила',
  'commands.group.scenes': 'сцены',
  'commands.group.talking': 'разговор',
  'commands.group.session': 'сессия',
  'commands.group.other': 'прочее',

  'msg.noSeatNamed': 'роли {seat} нет - есть: {seats}',
  'msg.mentionUsage': 'как использовать: {usage}',
  'msg.npcMention': 'за роль {seat} играет бот – спросите его о чём угодно: /ask {seat} <вопрос>',
  'msg.askUsage': 'как использовать: /ask <роль> <ваш вопрос или mood|decision:id|concern:id>',
  'msg.viewUsage': 'как использовать: /view {panels} (сейчас: {panel})',
  'msg.panelSwitched': 'панель: {panel}',
  'msg.noPendingEvent':
    'нечего решать – доска покажет событие под "event:", когда директор его запустит',
  'msg.doUsage': 'как использовать: /do <действие> [роль] – ваши: {verbs}',
  'msg.doNoSeat': 'как использовать: /do <действие> [роль] – у вас нет роли; сначала /claim <роль>',
  'msg.doNeedsSeat': '/do {verb} нужна роль – у вас её нет, назовите её: /do {verb} <роль>',
  'msg.usage': 'как использовать: {usage}',
  'msg.nearMiss': 'команды /{cmd} нет – вероятно, вам нужно: {meant}',
  'msg.unknownCommand':
    'неизвестная команда: {text} – /help покажет, что можно сейчас, /commands — весь список',

  'app.welcome': 'добро пожаловать — введите "login", чтобы начать.  ("diag" — диагностика)',
  'app.server': 'сервер: {url}',
  'app.restored':
    'восстановлена сохранённая сессия (только чтение) – введите "login", чтобы войти и открыть её',
  'app.signedIn': 'вход выполнен как {player}',
  'app.resumed': 'с возвращением, {player} – тот же игрок, что и в прошлый раз',
  'app.loginFailed':
    'вход не удался: {error} – введите "login" ещё раз или "diag", если повторяется',
  'app.loginFirst': 'сначала войдите (введите "login")',
  'app.created': 'создана "{name}" — {id}',
  'app.joinCode': 'код входа: {code}',
  'app.shareHint': 'введите "share", чтобы получить ссылку на эту сессию для другого устройства',
  'app.createFailed':
    'создать не удалось: {error} – попробуйте ещё раз или введите "diag", если повторяется',
  'app.joined': 'вход выполнен: {id}',
  'app.joinFailed':
    'войти не удалось: {error} – проверьте id и код входа или введите "list" для открытых сессий',
  'app.usage.join': 'как использовать: join <sessionId> [joinCode]',
  'app.shareFirst': 'сначала откройте сессию, затем введите "share"',
  'app.shareLink': 'ссылка: {link}',
  'app.shareNote':
    'открыв её на другом устройстве, вы войдёте в эту сессию и продолжите с текущего места.',
  'app.sharePrivate': '(закрытая сессия? код входа видит только владелец)',
  'app.invitation': 'приглашение: сессия {id}',
  'app.copied': '(скопировано в буфер обмена)',
  'app.listed': 'ваших: {mine}, открытых сессий: {n}',
  'app.listFailed':
    'список не загрузился: {error} – введите "list" ещё раз или "diag", если повторяется',
  'app.reconnecting': 'переподключение… (только чтение, пока сервер не подтвердит)',
  'app.noActiveSession': 'нет активной сессии – введите "list" и число или: create <имя> game',
  'app.sendFailed':
    'отправить не удалось: {error} – ничего не ушло; повторите, когда соединение станет live',
  'app.askFailed': 'не удалось спросить {seat}: {error}',
  'app.retroFailed': 'ретро не загрузилось: {error} – повторите /retro через мгновение',
  'app.notAGame': '  эта сессия создана без сценария – /leave, затем: create <имя> game',
  'app.leaveFailed': 'выйти не удалось: {error} – клиент всё равно выходит',
  'app.diagUnavailable':
    'диагностика недоступна в этой среде – делать нечего; странице диагностики нужна адресная строка браузера',
  'app.reloading': 'перезагрузка для применения обновления…',
  'app.updateReady': 'готова новая версия — введите "reload", чтобы обновиться.',
  'app.updateAvailable': 'доступно обновление: {instructions}',
  'app.updateRequired': 'требуется обновление: {reason} — перезагрузка…',
  'app.tokenRefreshFailed': 'не удалось обновить токен — войдите снова',
  'app.sessionExpired': 'сессия истекла — войдите снова',
  'app.authRefreshing': 'авторизация: {code} — обновляем',
  'app.nameLooksLikeCommand':
    '"{name}" похоже на команду, а не на имя – оно станет вашим именем для всей команды.',
  'app.identityExists':
    'в этом браузере уже есть личность ({player}, команд: {teams}) – новая потеряет её роли. Введите "login" без имени, чтобы её сохранить.',
  'app.identityConfirm': 'чтобы всё равно использовать это имя, введите: login {name}',
  'app.notice': 'уведомление: {message}',
  'app.noticeClaim': 'уведомление: {message} Введите: /claim {seat}',
  'app.noticeObserver':
    'уведомление: {message} Введите "help", чтобы узнать, что ещё доступно, или: create <name> game',
  'app.gameRefused': 'игра: {message}',
  'app.gameActionFailed': 'действие не выполнено: {error}',

  'beat.digest': 'что произошло с прошлого стендапа (ниже)',
  'beat.reveals': 'каждая роль может вынести один личный пункт — /share <роль> <item>',
  'beat.agenda': 'пройти очередь решений — /decide /defer /consult /vote /approve',
  'beat.commitments': 'кто что делает — /work <item> <роль>',
  'beat.objective': 'сформулировать цель — /objective <текст>',
  'beat.keyResults': 'добавить ключевые результаты — /kr <id> <выражение>',
  'beat.review': 'перечитать доску, прежде чем брать обязательства',
  'beat.docket': 'вынести риски, ради которых стоит собираться — /concern <роль> <тема>',
  'beat.motions': 'внести решения по повестке — /raise <id>',
  'beat.deliberation': 'обсудить, проголосовать, согласовать — /consult /vote /approve',
  'beat.resolution': 'закрыть то, что можно — /decide <id> <вариант>',
  'beat.close': 'настроение и разошлись — /mood <роль> <настроение>, затем /beat',

  'digest.quiet': '    с прошлого стендапа: ничего не произошло.',
  'digest.since': '    с раунда {round}: {parts}',
  'digest.also': '    а также: {parts}',
  'digest.rounds': 'раундов: {n}',
  'digest.events': 'событий разрешено: {n}',
  'digest.decisions': 'решений закрыто: {n}',
  'digest.work': 'задач выпущено: {n}',
  'digest.hires': 'наймов состоялось: {n}',
  'digest.fuses': 'отложенных эффектов сработало: {n}',
  'digest.timedOut': 'решений ушло в худший вариант по сроку: {n}',
  'digest.escalated': 'рисков эскалировано: {n}',
  'digest.promises': 'обещаний нарушено: {n}',

  'decisions.title': 'решения:',
  'decisions.none': '  (открытых нет) — /raise <id> [роль] открывает заготовленное решение',
  'decisions.options': '    варианты: {options} (по сроку сработает худший: {worst})',
  'decisions.needs': '    не хватает: {blocker}',
  'decisions.blocker.consults': 'ещё консультаций: {n} — /consult {id} <роль>',
  'decisions.blocker.votes': 'ещё голосов до кворума: {n} — /vote {id} <роль> <вариант>',
  'decisions.blocker.approval': 'согласование от {approver} — /approve {id} <роль>',
  'decisions.approver': 'согласующего',

  'policies.proposed': '  предложено (ещё не принято):',
  'policies.proposedName': '    {name} ({id})',
  'policies.proposedProse': '      {prose}',
  'policies.proposedEffects': '      каждый раунд: {effects}',
  'policies.proposedDownside': '      цена: {effects}',
  'policies.proposedAdopt': '      /adopt {id} — ввести в силу',
  'policies.title': 'правила:',
  'policies.none': '  (ничего не принято) — правила появляются после решения по мотиву',
  'policies.repealed': '    отменено в r{round}',

  'me.title': 'вы:',
  'me.quietWords': 'сказано лично (видите только вы):',
  'me.asked': '  вы спросили {seat} про {about}',
  'me.heard': '    услышано: “{quote}”',
  'me.noSeat': '  роли пока нет — /claim <роль>, чтобы занять',
  'me.takeover': '  вы наблюдаете. Можно принять роль, которую сейчас закрывают:',
  'me.takeoverFrom': 'принять роль {title} у {who}',
  'me.noVerbs': 'без действий',
  'me.seat': '  роль {seat} · свободная ёмкость {capacity} · {checkIn}',

  'board.header': 'игра – раунд {round}{clock} · {phase} · {status}',
  'board.situation':
    '  обстановка: риск {risk} · риски {concerns}{heat} · в работе {wip} · запросы {reqs} · решения {decisions} · правила {policies}',
  'board.win': '  победа: {conditions}',
  'board.lose': '  поражение: {conditions}',
  'board.collapse': '  крах: {conditions}',
  'board.endless': '  без дедлайна – компания живёт, пока живёт',
  'board.advance': '  переход в {phase}: {conditions}',
  'board.milestone': '  вехи: {chain}',
  'board.milestoneHere': '[{name}]',
  'board.milestoneCrossed': '{name}: день {day}',
  'board.milestoneUnlocks': '  {name} открывает: {unlocks}',
  'board.goalOr': ' или ',
  'board.heat': ' (накал {n})',
  'board.armed': '  {metric} сработает, когда {condition}',
  'board.sceneOffered':
    '  {kind} собрался — /beat, чтобы провести, или продолжайте: встреча закроется сама',
  'board.scene': '  сцена: {kind} — {beat} ({index}/{count}){shared}',
  'board.sceneShared': ', вынесено {n}',
  'board.thisBeat': '    этот такт: {prompt}',
  'board.seats': '  роли:',
  'board.can': '  может: {verbs}',
  'board.whoAreYou': '  кто вы в этой команде?',
  'board.pickRole': '    {title} — /claim {seat}',
  'board.pickRolePitched': '    {title} — {pitch}   /claim {seat}',
  'board.pickRecommended': '    {title} — {pitch}   /claim {seat}   ← начните отсюда',
  'board.takeoverOnly': '  все роли за кем-то закреплены. Вы наблюдаете.',
  'board.backlog': '  бэклог:',
  'board.company': '  {name}',
  'board.promise': '  вы обещали {who}: {what} — к р{round}   /kept {id} · /broke {id}',
  'board.promiseOverdue':
    '  ⚠ просрочено для {who}: {what} — было к р{round}   /kept {id} · /broke {id}',
  'board.stakeholder': '  {who}: {band}',
  'board.stakeholderSteady': 'пока без претензий',
  'board.stakeholderImpatient': 'начинает давить',
  'board.stakeholderSpent': 'терпение кончилось',
  'board.upcoming': '  дальше: {scene} через {rounds} раунд(ов)',
  'board.upcomingNow': '  дальше: {scene} на открытии раунда',
  'board.event': '  событие: {id}',
  'board.eventConsequence': '  ← это следствие более раннего решения',
  'board.eventScripted': '  ← это должно было случиться',
  'board.eventReveal': '    видите только вы: {reveal}',
  'board.eventOption': '    /resolve {id}  {label}{odds}',
  'board.eventUnlabelled': '(без названия)',
  'board.bet': ' [ставка – {odds}]',
  'board.oddsUnstated': 'шансы не указаны',
  'board.decisions': '  решения: {summary} — /view decisions',
  'board.policyProposed': '  предложено правило: {names} — /view policies, прочитайте до принятия',
  'board.decisionYours': 'ваше',
  'board.okrs': '  цели:',
  'board.objective': '    цель: {objective}',
  'board.you': '  вы: {seat} (свободно {capacity}, {checkIn})',

  'fidelity.line': 'режим: {rung} – потрачено {spent} из {ceiling} ({percent}%)',
  'fidelity.disabled': '  больше не работает: {purposes}',
  'milestone.header': '{headline}',
  'milestone.scripted':
    '  (\u0441\u043e\u0431\u0440\u0430\u043d\u043e \u0438\u0437 \u0437\u0430\u043f\u0438\u0441\u0435\u0439 \u0434\u0432\u0438\u0436\u043a\u0430)',
  'milestone.writing':
    '  \u0433\u043e\u0442\u043e\u0432\u0438\u043c \u0437\u0430\u043f\u0438\u0441\u044c\u2026',
  'milestone.none':
    '\u043f\u043e\u043a\u0430 \u043d\u0438 \u043e\u0434\u043d\u043e\u0439 \u0432\u0435\u0445\u0438 \u043d\u0435 \u043f\u0440\u043e\u0439\u0434\u0435\u043d\u043e.',
  'retro.header': 'ретро – {headline}',
  'retro.survival':
    '  выживание {score} = итог {final} + пик {peak}×{peakWeight} + раундов {rounds}×{roundWeight} + вех {gates}×{gateWeight} (треугольно)',
  'retro.okrs': '  цели: {grades}',
  'retro.concerns': '  риски: {concerns}',
  'retro.motifs': '  мотивы: {motifs}',
  'retro.private': 'ваше ретро (видите только вы):',

  'refuse.noOpenScene': '  сцена не открыта – /scene standup · /scene planning · /scene governance',
  'refuse.sceneAlreadyOpen': '  сцена уже идёт – /beat переводит её к следующей части',
  'refuse.notInStandup': '  этот ход относится к стендапу – /scene standup открывает его',
  'refuse.slotUsed': '  вы уже использовали свой слот на этом стендапе',
  'refuse.seatTaken': '  роль {value} занята – /view guide покажет, кто где и какие роли свободны',
  'refuse.alreadySeated': '  у вас уже есть роль – сначала /release',
  'refuse.notSeated':
    '  у вас нет роли – /claim <роль>, а /view guide расскажет, что каждая делает',
  'refuse.unknownSeat': '  роли с таким именем нет – /view guide перечислит их',
  'refuse.overCapacity':
    '  у этой роли не осталось ёмкости в этом раунде – /checkin закрывает раунд',
  'refuse.sessionStarted': '  сессия уже идёт – /help покажет, что в ней можно делать',
  'refuse.noOpenGate': '  сейчас ничего не ждёт на воротах',
  'refuse.krDuplicate':
    '  ключевой результат с таким id уже есть – выберите другой или сначала /rmkr {value}',
  'refuse.tooManyKrs':
    '  ключевых результатов уже слишком много – /rmkr <id>, чтобы освободить место',
  'refuse.unknownKr':
    '  ключевого результата с таким id нет – доска перечисляет их в разделе "цели:"',
  'refuse.krGraded': '  этот ключевой результат уже оценён – оценка это история, а не состояние',
  'refuse.decisionNotOwner': '  это решение не ваше – /view decisions назовёт владельца',
  'refuse.decisionUnknown': '  решения с таким id нет – /view decisions покажет открытые',
  'refuse.decisionUnknownOption': '  это не один из его вариантов – /view decisions перечислит их',
  'refuse.decisionConsults': '  ему ещё нужны консультации – /consult <id> <роль>',
  'refuse.decisionQuorum': '  ему ещё нужны голоса – /vote <id> <роль> <вариант>',
  'refuse.decisionApproval': '  оно ждёт согласования – /approve <id> <роль>',
  'refuse.workUnknown': '  задачи с таким id нет – доска перечисляет их в разделе "бэклог:"',
  'refuse.workDuplicate': '  такой id задачи уже занят – выберите другой',
  'refuse.workNotStartable': '  эту задачу пока нельзя начать – /view board покажет её состояние',
  'refuse.backlogFull': '  бэклог заполнен – /abandon <id>, чтобы освободить место',
  'refuse.notAGame': '  в этой сессии не запущен сценарий – /leave, затем: create <имя> game',

  'okr.none':
    'ничего измеримого не вернулось – напишите ключевой результат сами: /kr <id> <выражение>',
  'okr.objective': 'цель: {objective}   (принять: /objective {objective})',
  'okr.keyResult': '  {description}',
  'okr.adopt': '    /kr {id} {expression}',
  'okr.coaching': '  Инес (консультант): {coaching}',
  'okr.rejected':
    '  (отброшено предложений: {n} – они не компилируются против метрик этого сценария)',
  'okr.failed': 'не удалось разобрать цель: {error}',
  'okr.usage': 'как использовать: /okr <чего команда пытается достичь>',
  'policy.preview': 'предложенное правило: {name} ({id})',
  'policy.prose': '  {prose}',
  'policy.effects': '  делает: {effects}',
  'policy.downside': '  стоит: {effects}',
  'policy.adopt': '  принять: /adopt {id}',
  'policy.refused': 'это соглашение не компилируется в правило для этой сессии:',
  'policy.reason': '  {rule}: {message}',
  'policy.unavailable': 'компиляция правил сейчас недоступна – повторите через мгновение',
  'policy.failed': 'не удалось скомпилировать правило: {error}',
  'policy.noDraft':
    'нет предложенного правила {id} – /policy <о чём договорилась команда> создаст его',
  'policy.usage': 'как использовать: /policy <договорённость своими словами>',
  'policy.adoptUsage': 'как использовать: /adopt <id предложенного правила>',
  'brief.title': 'об этой сессии:',
  'brief.deadline':
    '  часы: раунд {round} из {deadline} - когда время выйдет, сессия закончится как есть',
  'brief.noDeadline': '  часы: раунд {round}, жёсткого срока нет',
  'brief.objective': '  цель: {objective}',
  'brief.noObjective': '  цель: её ещё никто не поставил – /objective <текст> на планировании',
  'brief.who': '  люди: {humans}   ·   боты: {bots}',
  'brief.win': '  чтобы победить: {conditions}',
  'brief.lose': '  держать: {conditions}',
  'brief.ends':
    '  сессия кончается, когда выйдет срок или разрешится сценарий; дальше ретро расскажет, что произошло',
  'brief.footer': '  что я могу сейчас: /help   ·   зачем нужна каждая роль: /view guide',
  'chrome.you': 'вы: {seat} ({checkIn})',
  'chrome.noSeat': 'роли нет',
  'board.claim': '[занять]',
  'chrome.clock': 'r{round} · {phase}',
  'app.retryAfter': '  слишком много запросов – подождите {seconds} с и повторите',

  'diag.unknown': 'неизвестно: {cmd} (run · repair · bundle · back)',
  'diag.update': 'обновление: {action} ({reason})',
  'diag.cachesCleared': 'кэши очищены (IndexedDB). Перезагрузите страницу.',
  'diag.repairFailed': 'починить не удалось: {error} – перезагрузите страницу и повторите repair',
  'diag.bundleDownloaded': 'диагностический архив скачан (без токенов и персональных данных).',
  'diag.bundleReady': 'архив готов ({bytes} байт, данные вычищены).',
  'diag.title': 'TEAM SIMULATOR — диагностика (доктор)',
  'diag.server': 'сервер: {url}',
  'diag.typeRun': 'введите "run", чтобы начать.',
  'diag.summary': 'итог: {pass} ок · {warn} предупр. · {fail} ошибок',
  'diag.commands': 'команды: run · repair (очистить кэши) · bundle (скачать) · back',
};

const CATALOGS: Record<Lang, Partial<Record<MessageKey, string>>> = { en: EN, ru: RU };

let active: Lang = 'en';

/** Sets the interface language. Anything that is not a known catalog falls back to English. */
export function setLanguage(lang: string): void {
  active = lang.toLowerCase().startsWith('ru') ? 'ru' : 'en';
}

/** The active interface language. */
export function language(): Lang {
  return active;
}

/**
 * One translated line. Missing RU falls back to EN rather than to the key: a stray English sentence is
 * survivable, a raw `board.roster` on screen is not.
 */
export function t(key: MessageKey, params?: Record<string, string | number>): string {
  const text = CATALOGS[active][key] ?? EN[key];
  if (!params) {
    return text;
  }

  return text.replace(/\{(\w+)\}/g, (whole, name: string) =>
    name in params ? String(params[name]) : whole,
  );
}

/** Exported for the coverage test: every key EN defines, RU has to define too. */
export const CATALOG_KEYS = Object.keys(EN) as MessageKey[];

/** Exported for the coverage test. */
export const RU_CATALOG = RU;
